import { Color } from "@repo/app-types/constants/colors";
import {
  getRecordingIntakeFailedImportErrorLabel,
  RECORDING_INTAKE_CUSTOMER_BULK_ACTION_MAX_IDS,
  RECORDING_INTAKE_NO_ERROR_CODE_KEY,
  type RecordingIntakeCustomerFailureRow,
  type RecordingIntakeId,
} from "@repo/app-types/entities/recordingIntake";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { Pill } from "@repo/ui/Pills/pill";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Dialog, type SortDescriptor } from "react-aria-components";
import {
  useRecordingS3IntakeFailedImportsCountsByCodeQuery,
  useRecordingS3IntakeFailedImportsInfiniteQuery,
  useReprocessRecordingS3IntakeFailedImportsMutation,
  useSkipRecordingS3IntakeFailedImportsMutation,
} from "../../../api/entities/recordingS3IntakeIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

/** A row action, and the rows it targets — one row, or the bulk selection. */
type FailedImportAction = "reprocess" | "skip";
interface PendingAction {
  action: FailedImportAction;
  ids: readonly RecordingIntakeId[];
}

// The row's `id` (the intake UUID) is not shown — it's meaningless to a
// customer — but it stays the row identity used for selection and the
// reprocess/skip payloads.
const baseColumns: readonly EntityTableColumnDefinition<RecordingIntakeCustomerFailureRow>[] =
  [
    {
      id: "intaken_at",
      header: "Received at",
      isRowHeader: true,
      allowsSorting: true,
      getTextValue: (row) => row.intakenAt,
      renderCell: (row) => (
        <span className="whitespace-nowrap text-foreground">
          {DateTime.fromISO(row.intakenAt).toLocaleString(
            DateTime.DATETIME_MED,
          )}
        </span>
      ),
    },
    {
      id: "object_key",
      header: "File",
      getTextValue: (row) => row.objectKey,
      // Plain, selectable text — deliberately no button in this cell. A focusable
      // child makes react-aria's table treat the whole cell as interactive and
      // swallow pointer-down, which blocks drag-selecting the key (triple-click
      // still selects the whole thing). Wrapping instead of a single truncated
      // line keeps it fully visible and find-in-page-matchable, and
      // `whitespace-pre-wrap` preserves the key's exact spacing so a pasted value
      // matches a search.
      renderCell: (row) => (
        <span className="block max-w-[22rem] break-all whitespace-pre-wrap text-foreground">
          {row.objectKey}
        </span>
      ),
    },
    {
      id: "error",
      header: "Error",
      getTextValue: (row) => row.errorCode ?? "",
      renderCell: (row) => {
        const label = getRecordingIntakeFailedImportErrorLabel(row.errorCode);
        if (!label) {
          return <span className="text-muted-foreground">—</span>;
        }
        // The friendly label on the pill; the raw message in the tooltip.
        return (
          <span title={row.errorMessage ?? undefined}>
            <Pill label={label} size={Size.SMALL} color={Color.Red} />
          </span>
        );
      },
    },
  ];

/**
 * The leading selection column: a per-row checkbox plus a "select all" header
 * that reflects/toggles the currently-loaded rows (indeterminate when only some
 * are selected). Only rendered for teammates who can act on the rows.
 */
function selectionColumn(
  rows: readonly RecordingIntakeCustomerFailureRow[],
  selectedIds: ReadonlySet<RecordingIntakeId>,
  onToggleRow: (id: RecordingIntakeId, shouldSelect: boolean) => void,
  onToggleAll: (shouldSelect: boolean) => void,
): EntityTableColumnDefinition<RecordingIntakeCustomerFailureRow> {
  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = rows.some((row) => selectedIds.has(row.id));
  return {
    id: "select",
    className: "w-10",
    header: (
      <Checkbox
        isChecked={allSelected}
        isIndeterminate={!allSelected && someSelected}
        onChange={onToggleAll}
        accessibleLabel="Select all loaded rows"
      />
    ),
    renderCell: (row) => (
      <Checkbox
        isChecked={selectedIds.has(row.id)}
        onChange={(shouldSelect) => onToggleRow(row.id, shouldSelect)}
        accessibleLabel={`Select ${row.objectKey}`}
      />
    ),
  };
}

/** The trailing per-row actions: Reprocess and Skip, gated behind a confirm. */
function actionsColumn(
  onAction: (action: FailedImportAction, id: RecordingIntakeId) => void,
): EntityTableColumnDefinition<RecordingIntakeCustomerFailureRow> {
  return {
    id: "actions",
    header: "Actions",
    renderCell: (row) => (
      <div className="flex justify-end gap-2 whitespace-nowrap">
        <Button
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
          onClick={() => onAction("reprocess", row.id)}
        >
          Reprocess
        </Button>
        <Button
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
          onClick={() => onAction("skip", row.id)}
        >
          Skip
        </Button>
      </div>
    ),
  };
}

/** The bulk action bar shown when ≥1 row is selected. */
const BulkActionBar: FC<{
  count: number;
  isBusy: boolean;
  onReprocess: () => void;
  onSkip: () => void;
  onClear: () => void;
}> = ({ count, isBusy, onReprocess, onSkip, onClear }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-md border border-border-light bg-muted px-3 py-2">
    <span className="text-sm font-medium text-foreground">
      {count.toLocaleString()} selected
    </span>
    <div className="flex items-center gap-2">
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
        isDisabled={isBusy}
        onClick={onReprocess}
      >
        Reprocess
      </Button>
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
        isDisabled={isBusy}
        onClick={onSkip}
      >
        Skip
      </Button>
      <Button
        size={Size.SMALL}
        variant={ButtonVariant.Tertiary}
        onClick={onClear}
      >
        Clear
      </Button>
    </div>
  </div>
);

/**
 * The **Failed imports** tab: the caller company's own `failed`
 * `recording_intake` rows, with infinite scroll and — for teammates who can
 * update integrations — the ability to reprocess (re-import) or skip them,
 * per-row or in bulk over the loaded rows. The customer twin of the HQ
 * `/recording-s3-intake` triage page, scoped to one company and to `failed`.
 */
export const RecordingS3IntakeFailedImportsTab: FC = () => {
  const dispatch = useAppDispatch();
  const ability = useTeammateAuthorization();
  const canUpdate = ability.can(
    PermissionActions.UPDATE,
    PermissionSubjects.INTEGRATIONS,
  );

  // Only the "Received at" column sorts; clicking its header flips the direction.
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "intaken_at",
    direction: "descending",
  });
  const order = sortDescriptor.direction === "ascending" ? "asc" : "desc";

  // The selected error-code chip: null = every code (the "All" chip), the
  // `__none__` sentinel = rows carrying no code, else a literal code. Part of the
  // query arg, so picking a chip narrows server-side and starts a fresh page 1.
  const [codeFilter, setCodeFilter] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRecordingS3IntakeFailedImportsInfiniteQuery({
    order,
    errorCode: codeFilter,
  });

  // The chip counts, scoped to company + `failed` only (never the selected
  // code), so they ride a separate query that doesn't refetch when a chip is
  // picked — the same split the HQ triage page makes.
  const { data: counts } = useRecordingS3IntakeFailedImportsCountsByCodeQuery();
  const countsByCode = counts?.countsByCode ?? null;

  const [reprocess, { isLoading: isReprocessing }] =
    useReprocessRecordingS3IntakeFailedImportsMutation();
  const [skip, { isLoading: isSkipping }] =
    useSkipRecordingS3IntakeFailedImportsMutation();
  const isBusy = isReprocessing || isSkipping;

  const [selectedIds, setSelectedIds] = useState<
    ReadonlySet<RecordingIntakeId>
  >(new Set());
  const [pending, setPending] = useState<PendingAction | null>(null);

  const rows = useMemo(
    () => data?.pages.flatMap((entry) => entry.rows) ?? [],
    [data],
  );

  // "Select all" only covers the rows one bulk action accepts.
  const selectableRows = useMemo(
    () => rows.slice(0, RECORDING_INTAKE_CUSTOMER_BULK_ACTION_MAX_IDS),
    [rows],
  );

  // Prune selection to the rows currently shown, so ids dropped by a refetch —
  // e.g. after a reprocess/skip moves them off this view — don't linger.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visible = new Set(rows.map((row) => row.id));
      const next = new Set<RecordingIntakeId>();
      for (const id of prev) {
        if (visible.has(id)) {
          next.add(id);
        }
      }
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  // The "All" chip's total, off the same uncapped counts that label the chips.
  const allCodesTotal = useMemo(
    () => (countsByCode ?? []).reduce((sum, entry) => sum + entry.count, 0),
    [countsByCode],
  );

  // Drop a code filter whose chip has gone — reprocessing or skipping the last
  // row of a code removes it from the counts, which would otherwise leave an
  // empty table filtered by an invisible chip. Guarded on the counts having
  // loaded so the filter isn't cleared during the fetch it just triggered.
  useEffect(() => {
    if (!codeFilter || !countsByCode) {
      return;
    }
    const isLive = countsByCode.some(
      (entry) =>
        (entry.code ?? RECORDING_INTAKE_NO_ERROR_CODE_KEY) === codeFilter,
    );
    if (!isLive) {
      setCodeFilter(null);
    }
  }, [codeFilter, countsByCode]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleRow = useCallback(
    (id: RecordingIntakeId, shouldSelect: boolean) => {
      setSelectedIds((prev) => {
        // Never let the selection grow past what one bulk action accepts — the
        // header "select all" already stops at the cap, and this bounds
        // hand-selection too, so the payload can never 400 against the server.
        if (
          shouldSelect &&
          !prev.has(id) &&
          prev.size >= RECORDING_INTAKE_CUSTOMER_BULK_ACTION_MAX_IDS
        ) {
          return prev;
        }
        const next = new Set(prev);
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const toggleAll = useCallback(
    (shouldSelect: boolean) => {
      setSelectedIds(
        shouldSelect ? new Set(selectableRows.map((row) => row.id)) : new Set(),
      );
    },
    [selectableRows],
  );

  const requestRowAction = useCallback(
    (action: FailedImportAction, id: RecordingIntakeId) =>
      setPending({ action, ids: [id] }),
    [],
  );

  const columns = useMemo(
    () =>
      canUpdate
        ? [
            selectionColumn(selectableRows, selectedIds, toggleRow, toggleAll),
            ...baseColumns,
            actionsColumn(requestRowAction),
          ]
        : baseColumns,
    [
      canUpdate,
      selectableRows,
      selectedIds,
      toggleRow,
      toggleAll,
      requestRowAction,
    ],
  );

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const selectedCount = selectedIdList.length;

  const runPending = useCallback(async () => {
    if (!pending) {
      return;
    }
    const { action, ids } = pending;
    setPending(null);
    try {
      if (action === "reprocess") {
        await reprocess(ids).unwrap();
      } else {
        await skip(ids).unwrap();
      }
      clearSelection();
    } catch (error) {
      // Surface the failure — the mutation error isn't rendered anywhere, so
      // without this the dialog just closes and the rows silently stay put. Keep
      // the selection so the user can retry.
      showNotification(dispatch, {
        id: `recording-s3-intake-${action}-error-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage:
          action === "reprocess"
            ? "Couldn't reprocess the selected imports"
            : "Couldn't skip the selected imports",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });
    }
  }, [pending, reprocess, skip, clearSelection, dispatch]);

  const pendingCount = pending?.ids.length ?? 0;
  const isSkip = pending?.action === "skip";

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        Call recordings that couldn&apos;t be imported. Reprocess one to try
        importing it again, or skip it to dismiss it.
      </p>

      {countsByCode && countsByCode.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <Pill
            label={`All (${allCodesTotal})`}
            size={Size.SMALL}
            color={codeFilter ? Color.Gray : Color.DarkBlue}
            onClick={() => setCodeFilter(null)}
          />
          {countsByCode.map((entry) => {
            const key = entry.code ?? RECORDING_INTAKE_NO_ERROR_CODE_KEY;
            const isActive = codeFilter === key;
            // Same friendly label as the table's Error column; the null-code
            // bucket has no label to look up, so name it explicitly.
            const label =
              getRecordingIntakeFailedImportErrorLabel(entry.code) ??
              "No error code";
            return (
              <Pill
                key={key}
                label={`${label} (${entry.count})`}
                size={Size.SMALL}
                color={isActive ? Color.Red : Color.Gray}
                onClick={() => setCodeFilter(isActive ? null : key)}
              />
            );
          })}
        </div>
      ) : null}

      {canUpdate && selectedCount > 0 ? (
        <BulkActionBar
          count={selectedCount}
          isBusy={isBusy}
          onReprocess={() =>
            setPending({ action: "reprocess", ids: selectedIdList })
          }
          onSkip={() => setPending({ action: "skip", ids: selectedIdList })}
          onClear={clearSelection}
        />
      ) : null}

      {/*
        EntityTable has no scroll container of its own; a bounded, scrollable box
        gives its sticky header a range to stick against and keeps a wide table
        from scrolling the whole page.
      */}
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-border-light">
        <EntityTable
          aria-label="Failed call recording imports"
          columns={columns}
          entities={rows}
          isLoading={isLoading}
          isError={isError}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
          onLoadMore={
            hasNextPage && !isFetchingNextPage
              ? () => void fetchNextPage()
              : undefined
          }
          emptyMessage="No failed imports"
        />
      </div>

      <CenteredModal
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
        isDismissable
      >
        <Dialog className="w-screen max-w-md space-y-4 p-4">
          <ModalHeader isCloseVisible>
            {isSkip ? "Skip failed imports" : "Reprocess failed imports"}
          </ModalHeader>
          <p className="text-sm text-muted-foreground">
            {isSkip
              ? `Dismiss ${pendingCount.toLocaleString()} failed import${pendingCount === 1 ? "" : "s"}. They'll be removed from this list.`
              : `Try importing ${pendingCount.toLocaleString()} recording${pendingCount === 1 ? "" : "s"} again.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
              slot="close"
            >
              Cancel
            </Button>
            <Button
              size={Size.MEDIUM}
              variant={ButtonVariant.Primary}
              onClick={() => void runPending()}
            >
              {isSkip ? "Skip" : "Reprocess"}
            </Button>
          </div>
        </Dialog>
      </CenteredModal>
    </div>
  );
};
